import { useMemo, useState } from 'react';
import { Badge, Button, Input, Select } from '@react-apps/design-system';
import { CATALOG_ROLES, hasAnyRole } from '@/shared/guards/roles';
import { useEffectiveRoles } from '@/shared/view-as/use-effective-roles';
import type { PerkPresetItem } from '../../../domain/entities/catalog.entity';
import {
  useAllPerkPresets,
  usePerkPresetItems,
} from '../../hooks/use-perk-presets';
import {
  describeGrantPolicyValues,
  grantPolicyLabel,
  perkTypeLabel,
} from '../../labels';
import type { ResolvedPerk } from '../../perk-index';

/** Sem busca, o catálogo inteiro empurraria o staging para fora da tela. */
const UNFILTERED_LIMIT = 10;
const FILTERED_LIMIT = 30;

interface PerkCatalogPanelProps {
  /** Já filtrados pelo scope do alvo, quando ele valida scope. */
  forms: ResolvedPerk[];
  /** Perks que o alvo já tem e não aceita repetidos — o botão fica travado. */
  blockedPerkIds: Set<string>;
  onAdd: (perkId: string) => void;
  /** Puxa itens de preset para o staging; o preset entra clonado, não vinculado. */
  onAddPresetItems: (presetId: string, items: PerkPresetItem[]) => void;
  /** Ainda carregando: a lista vazia aqui não quer dizer nada. */
  catalogLoading?: boolean;
  /** O catálogo não carregou — sem isso a falha fica igual a um catálogo vazio. */
  catalogError?: boolean;
  /**
   * Não deu para ler as roles de quem concede na plataforma do alvo: a oferta
   * pode estar incompleta, e a lista vazia não é falta de autoridade.
   */
  offerError?: boolean;
}

/**
 * A coluna da direita: de onde as linhas saem. Duas origens, mesmo destino — o
 * catálogo item a item e o preset em bloco. O preset **não** é aplicado direto:
 * ele despeja itens editáveis na esquerda, e nada é gravado antes do "Aplicar"
 * (§2 do plano — foi por isso que a aba "Preset" do modal antigo morreu).
 *
 * A segunda origem é **condicionada ao papel**, e aqui isso não é só UX: este
 * editor também é aberto pelo `adminDashboard.iamEditor` (modal de acessos do
 * perfil, em Usuários), e as rotas de preset (`GET /admin/perk-presets` e
 * `.../items`) são de `adminDashboard.administrator` no monolith. Sem a guarda,
 * o `PresetPicker` montaria e as duas queries voltariam 403. O catálogo item a
 * item continua visível para os dois: ele vem de `GET /admin/catalog/perks`,
 * que o `iamEditor` alcança por `catalog.perks.view`.
 */
export function PerkCatalogPanel({
  forms,
  blockedPerkIds,
  onAdd,
  onAddPresetItems,
  catalogLoading = false,
  catalogError = false,
  offerError = false,
}: PerkCatalogPanelProps) {
  const [query, setQuery] = useState('');
  const canUsePresets = hasAnyRole(useEffectiveRoles(), CATALOG_ROLES);

  // Forma depreciada **sai da oferta**: não se concede a gente nova. O que o alvo
  // já tem continua na coluna da esquerda (com badge) — isto aqui é só a fonte.
  const availableForms = useMemo(
    () => forms.filter(({ form }) => !form.deprecatedAt),
    [forms],
  );

  const knownPerkIds = useMemo(
    () => new Set(availableForms.map(({ form }) => form.perkId)),
    [availableForms],
  );

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return availableForms.slice(0, UNFILTERED_LIMIT);
    return availableForms
      .filter(({ group, form }) =>
        [group.name ?? '', group.key, group.type, form.grantPolicyKind]
          .join(' ')
          .toLowerCase()
          .includes(term),
      )
      .slice(0, FILTERED_LIMIT);
  }, [availableForms, query]);

  return (
    <div className="space-y-4">
      <div>
        <Input
          label="Catálogo"
          placeholder="Buscar por capacidade, key ou forma…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          helperText={
            query.trim()
              ? `${matches.length} resultado(s)`
              : `Mostrando ${matches.length} de ${availableForms.length} — digite para buscar.`
          }
        />
      </div>

      {offerError && !catalogLoading && !catalogError && (
        <p role="alert" className="text-sm text-red-600">
          Não foi possível conferir suas roles na plataforma deste perfil. A
          lista mostra só o que você concede pelas roles deste painel; feche e
          abra de novo para tentar outra vez.
        </p>
      )}

      {catalogLoading ? (
        <p role="status" className="text-(--color-text-secondary) text-sm">
          Carregando catálogo…
        </p>
      ) : catalogError ? (
        <p role="alert" className="text-sm text-red-600">
          Não foi possível carregar o catálogo. Feche e abra de novo; se
          persistir, confira se você ainda tem acesso a esta tela.
        </p>
      ) : availableForms.length === 0 ? (
        offerError ? null : (
          // Catálogo carregado e nada a oferecer: quase sempre é falta de
          // autoridade de quem concede, não de perks — o "0 de 0" mudo levou a
          // uma investigação em produção em 2026-10-06.
          <p
            role="status"
            className="text-sm text-amber-700 dark:text-amber-400"
          >
            Nada do catálogo pode ser concedido por você a este perfil. Para
            conceder, você precisa de uma das roles que cada perk aceita, aqui
            no painel ou no seu perfil desta plataforma.
          </p>
        )
      ) : matches.length === 0 ? (
        <p className="text-(--color-text-secondary) text-sm italic">
          Nenhum perk do catálogo bate com a busca.
        </p>
      ) : (
        <ul className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
          {matches.map(({ group, form }) => {
            const blocked = blockedPerkIds.has(form.perkId);
            return (
              <li
                key={form.perkId}
                className="border-(--color-border) flex items-center justify-between gap-2 rounded-md border px-3 py-2"
              >
                <div className="min-w-0">
                  <span className="text-(--color-text-primary) block truncate text-sm">
                    {group.name ?? group.key}
                  </span>
                  <span className="text-(--color-text-secondary) block truncate text-xs">
                    {perkTypeLabel(group.type)} ·{' '}
                    {grantPolicyLabel(form.grantPolicyKind)}
                  </span>
                </div>
                {blocked ? (
                  <Badge variant="default">Já no alvo</Badge>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => onAdd(form.perkId)}
                  >
                    Adicionar
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {canUsePresets && (
        <PresetPicker
          blockedPerkIds={blockedPerkIds}
          knownPerkIds={knownPerkIds}
          onAddPresetItems={onAddPresetItems}
        />
      )}
    </div>
  );
}

interface PresetPickerProps {
  blockedPerkIds: Set<string>;
  /** Perks visíveis ao alvo — um item fora disso não pode ser puxado. */
  knownPerkIds: Set<string>;
  onAddPresetItems: (presetId: string, items: PerkPresetItem[]) => void;
}

function PresetPicker({
  blockedPerkIds,
  knownPerkIds,
  onAddPresetItems,
}: PresetPickerProps) {
  const [presetId, setPresetId] = useState('');
  const { data: presets = [] } = useAllPerkPresets();
  const { data: items = [], isLoading } = usePerkPresetItems(presetId);

  const resolved = useMemo(
    () =>
      items.map((item) => ({
        item,
        blockedReason: !knownPerkIds.has(item.perkId)
          ? 'Indisponível aqui'
          : blockedPerkIds.has(item.perkId)
            ? 'Já no alvo'
            : null,
      })),
    [items, knownPerkIds, blockedPerkIds],
  );
  const pullable = resolved
    .filter((r) => r.blockedReason === null)
    .map((r) => r.item);

  if (presets.length === 0) return null;

  return (
    <div className="border-(--color-border) space-y-3 border-t pt-4">
      <Select
        label="Preset"
        value={presetId}
        onChange={(e) => setPresetId(e.target.value)}
        placeholder="Puxar de um preset…"
        options={presets.map((p) => ({ value: p.id, label: p.name }))}
        helperText={
          presets.find((p) => p.id === presetId)?.description ||
          'Os itens entram editáveis à esquerda; nada é gravado agora.'
        }
      />

      {presetId && isLoading && (
        <p className="text-(--color-text-secondary) text-sm">Carregando…</p>
      )}

      {presetId && !isLoading && items.length === 0 && (
        <p className="text-(--color-text-secondary) text-sm italic">
          Este preset não tem perks.
        </p>
      )}

      {resolved.length > 0 && (
        <>
          <ul className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
            {resolved.map(({ item, blockedReason }) => (
              <li
                key={item.id}
                className="border-(--color-border) flex items-center justify-between gap-2 rounded-md border px-3 py-2"
              >
                <span className="text-(--color-text-secondary) min-w-0 truncate text-xs">
                  {describeGrantPolicyValues(item)}
                </span>
                {blockedReason ? (
                  <Badge variant="warning">{blockedReason}</Badge>
                ) : (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => onAddPresetItems(presetId, [item])}
                  >
                    Puxar
                  </Button>
                )}
              </li>
            ))}
          </ul>
          <Button
            type="button"
            size="sm"
            disabled={pullable.length === 0}
            onClick={() => onAddPresetItems(presetId, pullable)}
          >
            Puxar {pullable.length} item(ns)
          </Button>
        </>
      )}
    </div>
  );
}
